import { uniqueId } from 'lodash';
import { uniqBy } from 'ramda';
import { GroupedChat } from '../../../../../../../services/modules/chats/chats.types';
import { isMainChat } from '../useChats';
import { ChatState, GroupedChatRoomsState } from './chatListReducer.types';

interface UpdateChatRoomsWithNewChatParams {
  newChat: ChatState;
  chatRooms: GroupedChatRoomsState[];
  groupOfChat?: GroupedChatRoomsState;
  newGroupOfChat?: GroupedChat;
}

export const updateChatRoomsWithNewChat = ({
  newChat,
  chatRooms,
  groupOfChat,
  newGroupOfChat,
}: UpdateChatRoomsWithNewChatParams): GroupedChatRoomsState[] => {
  const newChatIsMain = isMainChat(newChat);

  if (newGroupOfChat) {
    const chatToAdd = newChatIsMain ? { mainChat: newChat, chats: [] } : { chats: [newChat] };
    const newGroupedChatRoomState: GroupedChatRoomsState = {
      ...newGroupOfChat,
      metaId: uniqueId(),
      ...chatToAdd,
    };
    return [newGroupedChatRoomState, ...chatRooms];
  }

  if (groupOfChat) {
    const updatedChatRooms = chatRooms.map((group) => {
      if (group.metaId !== groupOfChat.metaId) {
        return group;
      }

      const newChats = newChatIsMain ? group.chats : [newChat, ...group.chats];
      const mainChat = newChatIsMain ? newChat : group.mainChat;

      const chatsWithoutDuplicates = uniqBy((chat) => chat.id, newChats);
      const chatIdsPresentInThisGroupingWithoutDuplicates = uniqBy(
        (chatId) => chatId,
        [...group.chatIdsPresentInThisGrouping, newChat.id],
      );

      return {
        ...group,
        mainChat,
        chats: chatsWithoutDuplicates,
        chatIdsPresentInThisGrouping: chatIdsPresentInThisGroupingWithoutDuplicates,
      };
    });

    const reorderedChatRooms = [
      ...updatedChatRooms.filter((group) => group.metaId === groupOfChat.metaId),
      ...updatedChatRooms.filter((group) => group.metaId !== groupOfChat.metaId),
    ];

    return reorderedChatRooms;
  }

  return chatRooms;
};

export const findChatByIdInGroup = (group: GroupedChatRoomsState, chatId: string) => {
  const chats = group.mainChat ? [group.mainChat, ...group.chats] : group.chats;
  return chats.find((chat) => chat.id === chatId);
};

export const findChatByCaseIdInGroup = (group: GroupedChatRoomsState, caseId: string) => {
  const chats = group.mainChat ? [group.mainChat, ...group.chats] : group.chats;
  return chats.find((chat) => chat.caseId === caseId);
};
